import { getDataType, isDataTypeLoaded } from '@medplum/core'
import { elementReference } from '@seen/core/resourceBindingRegistry'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { useQuery } from '#src/hooks/useQuery.ts'

export interface ElementBindingValueSet {
  // The canonical URL of the value set the element is bound to, without a
  // version; undefined when the element has no binding.
  valueSet: string | undefined
  loading: boolean
}

// The value set a FHIR element, such as
// http://hl7.org/fhir/StructureDefinition/Patient#Patient.gender, is bound
// to in its resource's schema.
export function useElementBindingValueSet(
  definition: string | undefined
): ElementBindingValueSet {
  const medplum = useMedplum()
  const enabled = Boolean(definition && elementReference(definition)?.path)
  const query = useQuery({
    queryKey: cacheKeys.elementBindingValueSet(definition ?? ''),
    enabled,
    staleTime: Infinity,
    queryFn: async (): Promise<string | null> => {
      const reference = elementReference(definition ?? '')
      if (!reference?.path) {
        return null
      }
      // requestSchema can reject on parts not needed here (the mock client's
      // search-parameter lookup) while still loading the base schema.
      await Promise.allSettled([medplum.requestSchema(reference.resourceType)])
      if (!isDataTypeLoaded(reference.resourceType)) {
        return null
      }
      const element = getDataType(reference.resourceType).elements[
        reference.path
      ]
      return element?.binding?.valueSet?.split('|')[0] ?? null
    },
  })
  return {
    valueSet: query.data ?? undefined,
    loading: enabled && query.isLoading,
  }
}
